import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import {
  ArrowRight,
  Building2,
  Check,
  CircleAlert,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  Server,
  ShieldCheck,
  X,
} from 'lucide-react';
import server from '../../api/server';
import { refreshAccessTokenIfNeeded } from '../../utils/dashboardAuth';

const parseJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const currentRedirect = () => encodeURIComponent(window.location.pathname + window.location.search);

const SCOPE_DETAILS = {
  'mcp:read': {
    title: 'Read analytics and Voice Agent data',
    description: 'View analytics, conversations, customers, saved reports, Voice Agent definitions, redacted configuration, and operational metrics; create downloadable data exports.',
  },
  'mcp:write': {
    title: 'Manage analytics drafts',
    description: 'Save draft module configurations and create or update custom reports. Live configuration cannot be published.',
  },
};

const ConsentPageShell = ({ children }) => (
  <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
    <header className="border-b border-gray-200 bg-white">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <img
          src={`${process.env.PUBLIC_URL || ''}/assets/anthro_logo_1.svg`}
          alt="Anthropod"
          className="h-9 w-auto"
        />
        <div className="flex items-center gap-2 text-xs font-medium text-gray-500 sm:text-sm">
          <LockKeyhole className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          Secure authorization
        </div>
      </div>
    </header>
    <main className="mx-auto flex w-full max-w-6xl justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      {children}
    </main>
  </div>
);

const LoadingState = () => (
  <ConsentPageShell>
    <div className="flex min-h-64 w-full max-w-3xl items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 text-sm font-medium text-gray-600" role="status">
        <LoaderCircle className="h-5 w-5 animate-spin text-indigo-600" aria-hidden="true" />
        Loading authorization request
      </div>
    </div>
  </ConsentPageShell>
);

const ErrorState = ({ message, onReturn }) => (
  <ConsentPageShell>
    <div className="w-full max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start gap-4 px-5 py-6 sm:px-8 sm:py-8">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-red-50 text-red-600">
          <CircleAlert className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Authorization request unavailable</h1>
          <p className="mt-2 text-sm leading-6 text-gray-600">{message}</p>
        </div>
      </div>
      <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-5 py-4 sm:px-8">
        <button
          type="button"
          onClick={onReturn}
          className="inline-flex h-10 items-center justify-center rounded px-4 text-sm font-medium text-white transition-colors bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Return to dashboard
        </button>
      </div>
    </div>
  </ConsentPageShell>
);

const McpOAuthConsent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestId = useMemo(() => new URLSearchParams(location.search).get('request_id'), [location.search]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!requestId) {
        setError('Authorization request is missing.');
        setLoading(false);
        return;
      }

      try {
        await refreshAccessTokenIfNeeded(server);
        let response = await server.getMcpOAuthConsentContext(requestId);
        if (response.status === 401) {
          await refreshAccessTokenIfNeeded(server, { force: true });
          response = await server.getMcpOAuthConsentContext(requestId);
        }
        const body = await parseJson(response);

        if (response.status === 401) {
          navigate(`/login?redirect=${currentRedirect()}`, { replace: true });
          return;
        }

        if (!response.ok) {
          throw new Error(body.error || 'Unable to load authorization request.');
        }

        if (active) {
          setContext(body.data || {});
          setError('');
        }
      } catch (err) {
        if (active) setError(err.message || 'Unable to load authorization request.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [requestId, navigate]);

  const redirectFromResponse = async (response) => {
    const body = await parseJson(response);
    if (!response.ok) throw new Error(body.error || 'Authorization failed.');
    if (body.redirect_url) {
      window.location.replace(body.redirect_url);
      return;
    }
    throw new Error('Authorization response did not include a redirect URL.');
  };

  const approve = async () => {
    setSubmitting(true);
    try {
      await refreshAccessTokenIfNeeded(server);
      const response = await server.approveMcpOAuthConsent({ request_id: requestId });
      await redirectFromResponse(response);
    } catch (err) {
      toast.error(err.message || 'Authorization failed.');
      setSubmitting(false);
    }
  };

  const deny = async () => {
    setSubmitting(true);
    try {
      await refreshAccessTokenIfNeeded(server);
      const response = await server.denyMcpOAuthConsent({
        request_id: requestId,
        denial_reason: 'User denied MCP authorization',
      });
      await redirectFromResponse(response);
    } catch (err) {
      toast.error(err.message || 'Unable to deny authorization.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <LoadingState />
        <ToastContainer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <ErrorState message={error} onReturn={() => navigate('/login')} />
        <ToastContainer />
      </>
    );
  }

  const approvedClientId = context?.approved_client_id;
  const approvedScopes = context?.approved_scopes || [];
  const clientAccessLabel = approvedClientId === '*' ? 'All clients' : approvedClientId;
  const serviceAccessLabel = approvedClientId === '*' ? 'All services across all clients' : 'All services for this client';

  return (
    <ConsentPageShell>
      <section className="w-full max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-start gap-4 px-5 py-6 sm:px-8 sm:py-7">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
            <ShieldCheck className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase text-indigo-600">Anthropod MCP</p>
            <h1 className="mt-1 text-xl font-semibold text-gray-900 sm:text-2xl">Authorize Anthropod access</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
              An MCP application is requesting access to Anthropod.
            </p>
          </div>
        </div>

        <div className="border-y border-indigo-100 bg-indigo-50 px-5 py-3 sm:px-8">
          <div className="flex items-start gap-3 text-sm text-indigo-900">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-indigo-600" aria-hidden="true" />
            <p>Review the application, data access, and permissions before continuing.</p>
          </div>
        </div>

        <div className="px-5 sm:px-8">
          <dl className="divide-y divide-gray-200">
            <div className="grid gap-2 py-5 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-6">
              <dt className="flex items-center gap-2 text-sm font-medium text-gray-600">
                <KeyRound className="h-4 w-4 text-gray-400" aria-hidden="true" />
                MCP application
              </dt>
              <dd className="break-all font-mono text-sm font-medium text-gray-900">{context?.mcp_client_id}</dd>
            </div>
            <div className="grid gap-5 py-5 sm:grid-cols-2 sm:gap-8">
              <div className="flex gap-3">
                <Building2 className="mt-0.5 h-5 w-5 flex-none text-indigo-600" aria-hidden="true" />
                <div>
                  <dt className="text-xs font-medium text-gray-500">Client access</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">{clientAccessLabel}</dd>
                </div>
              </div>
              <div className="flex gap-3">
                <Server className="mt-0.5 h-5 w-5 flex-none text-indigo-600" aria-hidden="true" />
                <div>
                  <dt className="text-xs font-medium text-gray-500">Service access</dt>
                  <dd className="mt-1 text-sm font-semibold text-gray-900">{serviceAccessLabel}</dd>
                </div>
              </div>
            </div>
          </dl>
        </div>

        <div className="border-t border-gray-200 px-5 py-6 sm:px-8">
          <h2 className="text-sm font-semibold text-gray-900">Permissions requested</h2>
          <div className="mt-4 divide-y divide-gray-100">
            {approvedScopes.map((scope) => {
              const details = SCOPE_DETAILS[scope] || {
                title: scope,
                description: 'Access granted by your Anthropod dashboard role.',
              };
              return (
                <div key={scope} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded bg-emerald-50 text-emerald-700">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{details.title}</p>
                      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{scope}</code>
                    </div>
                    <p className="mt-1 text-xs leading-5 text-gray-500">{details.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:flex sm:justify-end sm:px-8">
          <button
            type="button"
            onClick={deny}
            disabled={submitting}
            className="inline-flex h-10 items-center justify-center gap-2 rounded border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Deny
          </button>
          <button
            type="button"
            onClick={approve}
            disabled={submitting || !approvedScopes.length}
            className="inline-flex h-10 items-center justify-center gap-2 rounded bg-indigo-600 px-4 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            )}
            {submitting ? 'Authorizing' : 'Allow access'}
          </button>
        </div>
      </section>
      <ToastContainer />
    </ConsentPageShell>
  );
};

export default McpOAuthConsent;
